import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import idx from 'idx';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';
import { getResourceAllocations, importTimesheet, deleteResources } from '../../actions/timesheet.action';
import { getUserList } from '../../actions/user-management.action';
import { getProjectList, getTaskdropdownData } from '../../actions/project-management.action';
import { warningSnackBar } from '../../actions/common.action';
import { getGlobalSetting } from "../../actions/global-setting.action";
import { convertToReactSelectFormat, filterLockedSections } from "../../util/section-dropdown-format";
import { TIMESHEET_TASK_DROPDOWN_PARAMS } from './task-dropdown-options';
import './scss/weekly-timesheet.scss';
import TimeEntryForm from '../../components/timesheet/time-entry-form';
import TimesheetHeader from '../../components/timesheet/timesheet-header';
import TimesheetGridWrapper from '../../components/timesheet/timesheet-grid-wrapper';
import { useSnackbar } from 'notistack';
import { ValidateField, ValidatorTypes } from '../../util/validators';

function WeeklyTimesheetPage({ writePermission }) {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    // Redux selectors
    const user = useSelector(state => state.user.userData);
    const allowedModules = useSelector(state => state.project.allowedModules) || [];

    // Check if user has access to any projects
    const hasProjectAccess = allowedModules.length > 0;

    const [fields, setFields] = useState({
        projectId: {
            name: 'projectId',
            label: 'Project',
            value: '',
            placeholder: hasProjectAccess ? "Project" : "No projects available",
            disabled: !hasProjectAccess,
            items: [],
            touched: false,
            error: false,
            valid: true,
            validation: [ValidatorTypes.REQUIRED]
        }
    });

    const onProjectChange = (val) => {
        const { error, valid } = ValidateField({ value: val, validation: fields.projectId.validation });
        setFields(prev => ({
            ...prev,
            projectId: { ...prev.projectId, value: val, error, valid, touched: true }
        }));
    };

    const [weekStart, setWeekStart] = useState(moment().startOf('isoWeek').toDate());
    const [approved, setApproved] = useState(user?.permissions?.timesheetApprover === 2);

    const [users, setUsers] = useState([]);
    const [tasks, setTasks] = useState([]);

    const [timesheetData, setTimesheetData] = useState({});
    const [selectedUsers, setSelectedUsers] = useState([]);

    const [modalOpen, setModalOpen] = useState(false);
    const [editingContext, setEditingContext] = useState(null);
    const [currentEntries, setCurrentEntries] = useState([]);




    const [globalSettings, setGlobalSettings] = useState({
        startTime: null,
        endTime: null
    });

    useEffect(() => {
        init();
    }, []);

    useEffect(() => {
        if (fields.projectId.value) {
            loadTasks(fields.projectId.value);
        }
        if (fields.projectId.value && weekStart) {
            loadTimesheetData();
        }
    }, [fields.projectId.value, weekStart]);

    const init = async () => {
        if (user?.permissions?.timesheetApprover !== 2) {
            setApproved(false);
        }
        loadProjects();
        loadUsers();
        loadGlobalSettings();
    };

    const loadGlobalSettings = async () => {
        const response = await dispatch(getGlobalSetting('LETTERHEAD'));
        const letterheadData = idx(response, _ => _.data) || {};
        const rawStart = idx(letterheadData, _ => _.start_time) || '08:00';
        const rawFinish = idx(letterheadData, _ => _.finish_time) || '17:00';

        const startTime = moment.utc(rawStart, 'HH:mm').toDate();
        const finishTime = moment.utc(rawFinish, 'HH:mm').toDate();

        setGlobalSettings({ startTime, endTime: finishTime });
    };

    const loadProjects = async () => {
        // Skip API call if user has no allowed modules
        if (!hasProjectAccess) {
            setFields(prev => ({
                ...prev,
                projectId: { ...prev.projectId, items: [] }
            }));
            return;
        }
        const list = await dispatch(getProjectList(null, 'PROJECT'));
        if (Array.isArray(list)) {
            const formattedProjects = list.map(p => ({ key: p.name, value: p.id }));
            setFields(prev => ({
                ...prev,
                projectId: { ...prev.projectId, items: formattedProjects }
            }));
        }
    };

    const loadTasks = async (pid) => {
        if (!pid) return;
        const response = await dispatch(getTaskdropdownData(pid, TIMESHEET_TASK_DROPDOWN_PARAMS));
        const historicalSelectionIds = Object.values(timesheetData || {}).flatMap(userDays =>
            Object.values(userDays || {}).flatMap(dayEntries =>
                (dayEntries || []).flatMap(entry => [entry.taskId, entry.stageId]).filter(Boolean)
            )
        );

        /**
         * Section Filtering for Timesheet - Unified Attribution Logic
         * 
         * BEHAVIOR:
         * - Shows ALL sections (whether flagged or not)
         * - For sections flagged for attribution (is_attribution_level: true):
         *   → Hides tasks and child sections under that section
         *   → Section itself becomes selectable for attribution
         * - For non-flagged sections:
         *   → Shows tasks and child sections normally
         * 
         * @see section-dropdown-format.js - filterLockedSections() for full implementation
         */
        const filteredData = filterLockedSections(response || [], historicalSelectionIds);
        const formatted = convertToReactSelectFormat(filteredData, null, true);
        setTasks(formatted);
    };

    const loadUsers = async () => {
        const list = await dispatch(getUserList());
        if (Array.isArray(list)) {
            setUsers(list.map(u => ({
                key: `${u.firstName} ${u.lastName}`,
                value: u.id,
                hourlyRate: u.hourlyRate
            })));
        }
    };

    const loadTimesheetData = async () => {
        // Ensure we always fetch the full ISO week (Mon-Sun) that contains the selected date
        // or effectively the week the user is looking at.
        // If the user selects a date, they likely expect to see the week containing it
        const start = moment.utc(moment(weekStart).format('YYYY-MM-DD')).startOf('day');

        //api service will add one more day in the end date so we don't need to add one more day here
        // start date 01/11/25 then end date will be 07/11/25 in backend
        const end = moment.utc(moment(weekStart).add(5, 'days').format('YYYY-MM-DD')).endOf('day');

        const params = {
            projectId: fields.projectId.value,
            type: 'PROJECT',
            startTime: start.toISOString(),
            endTime: end.toISOString()
        };

        const allocations = await dispatch(getResourceAllocations(params));
        const newTimesheetData = {};
        const uniqueUserIds = new Set();

        if (Array.isArray(allocations)) {
            allocations.forEach(alloc => {
                const dateStr = moment(alloc.startTime).format('YYYY-MM-DD');
                if (!newTimesheetData[alloc.userId]) {
                    newTimesheetData[alloc.userId] = {};
                }
                if (!newTimesheetData[alloc.userId][dateStr]) {
                    newTimesheetData[alloc.userId][dateStr] = [];
                }

                const totalDuration = moment.duration(moment(alloc.endTime).diff(moment(alloc.startTime))).asHours();

                newTimesheetData[alloc.userId][dateStr].push({
                    id: alloc.id || uuidv4(),
                    regular: totalDuration > 0 ? totalDuration.toFixed(1) : '',
                    taskId: alloc.taskId || '',
                    stageId: alloc.stageId || '',
                    taskName: alloc.taskName || '',
                    sectionName: alloc.sectionHierarchy || '',
                    status: alloc.status || '',
                    isNew: false,
                    isDirty: false
                });
                uniqueUserIds.add(alloc.userId);
            });
        }

        setTimesheetData(newTimesheetData);

        setSelectedUsers(prevSelected => {
            const existingIds = new Set(prevSelected.map(u => u.value));
            const newUsers = users.filter(u => uniqueUserIds.has(u.value) && !existingIds.has(u.value));
            return [...prevSelected, ...newUsers];
        });
    };

    const handleAddRow = (userId) => {
        if (!fields.projectId.value) {
            dispatch(warningSnackBar("Please select a Project first"));
            return;
        }
        const user = users.find(u => u.value === userId);
        if (user) {
            setSelectedUsers(prev => {
                if (prev.find(u => u.value === userId)) return prev;
                return [...prev, user];
            });
        }
    };

    const handleRemoveRow = (userId) => {
        setSelectedUsers(selectedUsers.filter(u => u.value !== userId));
        // Data is preserved in timesheetData so it reappears if the user is added back
    };

    const handleCellClick = (userId, date) => {
        const dateStr = date.format('YYYY-MM-DD');
        const user = users.find(u => u.value === userId);
        const existingEntries = idx(timesheetData, _ => _[userId][dateStr]) || [];

        setEditingContext({ userId, userName: user?.key, date, dateStr });
        setCurrentEntries(_.cloneDeep(existingEntries));
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setEditingContext(null);
        setCurrentEntries([]);
    };



    const handleAddEntryToLocalList = (entryData, editingId = null) => {
        const reg = parseFloat(entryData.regular || 0);

        if (reg <= 0) {
            dispatch(warningSnackBar("Total hours must be greater than 0"));
            return;
        }

        if (editingId) {
            const editingEntry = currentEntries.find(e => e.id === editingId);
            if (editingEntry && !editingEntry.isNew) {
                const originalTotalHours = parseFloat(editingEntry.regular) || 0;
                if (reg > originalTotalHours) {
                    const entryIndex = currentEntries.findIndex(e => e.id === editingId);
                    const nextEntry = currentEntries[entryIndex + 1];
                    if (nextEntry && !nextEntry.isNew) {
                        const cumulativeHours = currentEntries.slice(0, entryIndex)
                            .reduce((acc, e) => acc + (parseFloat(e.regular) || 0), 0);
                        if (cumulativeHours + reg > cumulativeHours + originalTotalHours) {
                            dispatch(warningSnackBar(`Updated hours would overlap with the next entry. Please reduce hours or adjust the next entry first.`));
                            return;
                        }
                    }
                }
            }

            setCurrentEntries(prev => prev.map(e =>
                e.id === editingId
                    ? { ...e, regular: reg, taskId: entryData.taskId, stageId: entryData.stageId, taskName: entryData.taskName, taskPath: entryData.taskPath, sectionName: entryData.sectionName, isDirty: true }
                    : e
            ));
        } else {
            const entry = {
                id: uuidv4(),
                regular: reg,
                taskId: entryData.taskId,
                stageId: entryData.stageId,
                taskName: entryData.taskName,
                taskPath: entryData.taskPath,
                sectionName: entryData.sectionName,
                isNew: true,
                isDirty: false
            };
            setCurrentEntries([...currentEntries, entry]);
        }
    };

    const handleRemoveEntryFromLocalList = async (id) => {
        // Find the entry to check if it's a new entry or existing one
        const entryToDelete = currentEntries.find(e => e.id === id);

        // If it's an existing entry (not new), call the API to delete from database
        if (entryToDelete && !entryToDelete.isNew) {
            try {
                await dispatch(deleteResources("WORK", { allocationIds: [id] }));
                enqueueSnackbar('Entry deleted successfully', { variant: 'success', autoHideDuration: 3000 });
                // Refresh the data after deletion
                loadTimesheetData();
            } catch (error) {
                console.error('Error deleting entry:', error);
                enqueueSnackbar('Failed to delete entry', { variant: 'error', autoHideDuration: 3000 });
                return; // Don't remove from local list if API call failed
            }
        }

        // Remove from local list
        setCurrentEntries(currentEntries.filter(e => e.id !== id));
    };

    const handleSaveModal = () => {
        const { userId, dateStr } = editingContext;

        setTimesheetData(prev => {
            const newData = { ...prev };
            if (!newData[userId]) newData[userId] = {};
            newData[userId][dateStr] = currentEntries;
            return newData;
        });

        handleCloseModal();
    };

    const calculateDailyTotal = (userEntries) => {
        if (!userEntries) return 0;
        return userEntries.reduce((acc, curr) => acc + (parseFloat(curr.regular || 0)), 0);
    };

    const calculateWeeklyTotal = (userId) => {
        const userDays = timesheetData[userId];
        if (!userDays) return 0;
        let total = 0;
        Object.values(userDays).forEach(dayEntries => {
            total += calculateDailyTotal(dayEntries);
        });
        return total;
    };

    const calculateGrandDailyTotal = (dateStr) => {
        let total = 0;
        Object.values(timesheetData).forEach(userDays => {
            if (userDays[dateStr]) {
                total += calculateDailyTotal(userDays[dateStr]);
            }
        });
        return total;
    };

    const getDayDates = () => {
        const dates = [];
        const start = moment(weekStart); // Removed .startOf('isoWeek')
        for (let i = 0; i < 7; i++) {
            dates.push(moment(start).add(i, 'days'));
        }
        return dates;
    };

    const dates = getDayDates();

    const handleSaveTimesheet = async () => {
        if (!fields.projectId.value) {
            dispatch(warningSnackBar("Please select a Project"));
            return;
        }
        const flatItems = [];
        selectedUsers.forEach(user => {
            const userDays = timesheetData[user.value] || {};
            Object.keys(userDays).forEach(dateStr => {
                const entries = userDays[dateStr];
                entries.forEach(entry => {
                    // Only send new or modified entries
                    if (!entry.isNew && !entry.isDirty) return;

                    // Find project name as backend expects name for import
                    const projectObj = fields.projectId.items.find(p => p.value === fields.projectId.value);
                    const projectName = projectObj ? projectObj.key : '';

                    /**
                     * 
                     * Determines if the selected item is a section (stage) or a task:
                     * - If entry.stageId exists: It's a stage allocation
                     * - If entry.taskId exists: It's a task allocation
                     * 
                     * This enables both section-level and task-level timesheet attribution
                     */
                    const taskOption = tasks.find(t => t.value === (entry.stageId || entry.taskId));
                    flatItems.push({
                        id: entry.id,
                        userId: user.value,
                        project: projectName,
                        projectId: fields.projectId.value,
                        date: new Date(dateStr),
                        normalHours: parseFloat(entry.regular) || 0,
                        hourlyRate: user.hourlyRate,
                        stageId: entry.stageId || null,
                        taskId: entry.stageId ? null : (entry.taskId || null),
                        projectTask: (() => {
                            if (entry.taskPath) return entry.taskPath;
                            if (taskOption) {
                                if (taskOption.isStage) {
                                    // It's a section - use the label as projectTask
                                    return taskOption.label;
                                } else if (taskOption.stagePath) {
                                    // It's a task - build full path
                                    const stagePath = taskOption.stagePath.replace(/ > /g, '\\');
                                    return `${stagePath}\\${taskOption.key}`;
                                }
                            }
                            return entry.taskName || null;
                        })(),
                        isNew: entry.isNew || false,
                        isDirty: entry.isDirty || false,
                        isUpdate: !entry.isNew && entry.isDirty
                    });
                });
            });
        });

        if (flatItems.length === 0) {
            dispatch(warningSnackBar("No new or modified entries to save"));
            return;
        }

        const formattedItems = flatItems.map(item => {
            const dateStr = moment(item.date).format('YYYY-MM-DD');
            let start = new Date(item.date);
            let end = new Date(item.date);

            const baseStart = globalSettings.startTime;
            const startHours = baseStart ? baseStart.getUTCHours() : 8;
            const startMinutes = baseStart ? baseStart.getUTCMinutes() : 0;

            // Find all entries for this user/date to determine the sequence
            const userDayEntries = timesheetData[item.userId]?.[dateStr] || [];

            // Calculate start time based on the position of this entry in the day's sequence
            let currentStartTime = moment.utc(item.date).set({ hour: startHours, minute: startMinutes, second: 0, millisecond: 0 });

            for (const entry of userDayEntries) {
                if (entry.id === item.id) break;
                currentStartTime = currentStartTime.add(parseFloat(entry.regular) || 0, 'hours');
            }

            start = currentStartTime.toDate();

            const totalHours = item.normalHours;
            const startMoment = moment(start);
            const endMoment = startMoment.clone().add(totalHours, 'hours');
            end = endMoment.toDate();

            return {
                id: item.isNew ? '' : item.id,
                userId: item.userId,
                project: item.project,
                projectId: item.projectId,
                start_time: start,
                end_time: end,
                normalHours: item.normalHours,
                hourlyRate: item.hourlyRate,
                stageId: item.stageId || null,
                taskId: item.taskId || null,
                projectTask: item.projectTask,
                status: approved ? 'APPROVED' : 'PENDING',
                entryLocalDate: dateStr,
                isUpdate: item.isUpdate || false
            };
        });

        try {
            const response = await dispatch(importTimesheet({
                entries: formattedItems,
                globalStartTime: globalSettings.startTime,
                globalEndTime: globalSettings.endTime,
                source: 'weekly-timesheet'
            }));

            // Check if there were any import warnings (entries pushed to next day)
            if (response && response.warnings && response.warnings.length > 0) {
                response.warnings.forEach(w => {
                    enqueueSnackbar(w.warning, {
                        variant: 'info',
                        autoHideDuration: 6000
                    });
                });
            }

            // Check if there were any import errors
            const successCount = response?.successful || 0;
            const failedCount = response?.failed || 0;

            if (failedCount > 0) {
                enqueueSnackbar(`Import completed with ${successCount} successful and ${failedCount} failed entries.`, {
                    variant: 'warning',
                    autoHideDuration: 5000
                });
            } else if (successCount > 0) {
                enqueueSnackbar(`Successfully imported ${successCount} timesheet ${successCount === 1 ? 'entry' : 'entries'}.`, {
                    variant: 'success',
                    autoHideDuration: 5000
                });
            } else {
                enqueueSnackbar('No entries were saved. Please try again.', {
                    variant: 'warning',
                    autoHideDuration: 5000
                });
            }
            await loadTimesheetData();
        } catch (e) {
            console.error(e);
            dispatch(warningSnackBar("Error saving timesheet"));
        }
    };

    return (
        <div className="weekly-timesheet-container">
            {/* Permission Warning Banner */}
            {!hasProjectAccess && (
                <div className="alert alert-warning permission-warning-banner" role="alert">
                    You're not linked to any projects. Please contact your administrator for access.
                </div>
            )}

            {/* Header Area */}
            <div className="page-heading-row">
                <h2 className="content-heading text-uppercase">Weekly Timesheet</h2>
            </div>

            <TimesheetHeader
                projectId={fields.projectId}
                onProjectChange={onProjectChange}
                weekStart={weekStart}
                onWeekStartChange={setWeekStart}
                approved={approved}
                onApprovedChange={setApproved}
                hasProjectAccess={hasProjectAccess}
                writePermission={writePermission}
                user={user}
                onSaveTimesheet={handleSaveTimesheet}
            />

            <TimesheetGridWrapper
                dates={dates}
                selectedUsers={selectedUsers}
                timesheetData={timesheetData}
                users={users}
                projectId={fields.projectId.value}
                hasProjectAccess={hasProjectAccess}
                writePermission={writePermission}
                onAddRow={handleAddRow}
                onRemoveRow={handleRemoveRow}
                onCellClick={handleCellClick}
                calculateDailyTotal={calculateDailyTotal}
                calculateWeeklyTotal={calculateWeeklyTotal}
                calculateGrandDailyTotal={calculateGrandDailyTotal}
            />

            <TimeEntryForm
                tasks={tasks}
                handleAddEntryToLocalList={handleAddEntryToLocalList}
                currentEntries={currentEntries}
                handleRemoveEntryFromLocalList={handleRemoveEntryFromLocalList}
                modalOpen={modalOpen}
                handleCloseModal={handleCloseModal}
                handleSaveModal={handleSaveModal}
                editingContext={editingContext}
                writePermission={writePermission}
                onWarning={(msg) => enqueueSnackbar(msg, { variant: 'warning', autoHideDuration: 5000 })}
            />
        </div >
    );
}

export default WeeklyTimesheetPage;
